import { useRef, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import emailService from '@api/v1/emailService'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'
import { ACCEPTED_TITLE, ACCEPTED_NOTE, outcomeOfAnswer, outcomeOfFailure } from '@utils/emailComposeOutcome'

const TABS = [
  { id: 'templates', label: 'Templates' },
  { id: 'compose', label: 'Compose' },
  { id: 'settings', label: 'Mail Settings' },
]

const Email = () => {
  useDocumentTitle('Email')
  const [activeTab, setActiveTab] = useState('templates')

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-display text-gray-900">Email</h1>
        <p className="text-sm text-gray-500 mt-1">Templates, compose, and mail server settings</p>
      </div>

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit" role="tablist" aria-label="Email tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'compose' && <ComposeTab />}
      {activeTab === 'settings' && <SettingsTab />}
    </div>
  )
}

const TemplatesTab = () => {
  const query = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => emailService.getTemplates('0'),
  })
  const { data } = query
  /*
   * CC-53132. `error` alone was not enough: a paused fetch sets no error, calls
   * queryFn zero times and leaves `data` undefined, so the page said "No email
   * templates found" and headed the card "Email Templates (0)".
   *
   * `idle` is unreachable — no `enabled` option on this query — so it folds into the
   * loading branch rather than falling through every branch and rendering nothing.
   */
  const state = useReadState('email-templates', query)
  /*
   * The SHAPE half, the sibling failure `readShape.js` exists for. This was
   * `Array.isArray(data?.data) ? data.data : []`, which rewrote a body we could not
   * read as "no templates" just as silently as a paused read did — a 200 carrying
   * `{ templates: [...] }` instead of an array became a confident empty list.
   * A resolved read whose payload is not an array is a refusal, not a zero.
   */
  const shapeOk = Array.isArray(data?.data)
  const ready = state === 'ready' && shapeOk
  const unreadable = state === 'blocked' || state === 'failed' || (state === 'ready' && !shapeOk)

  const templates = shapeOk ? data.data : []

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="px-5 py-4 border-b border-gray-100">
        {/* The count is a claim, so it renders only once something has been counted. */}
        <h2 className="text-base font-semibold text-gray-900">
          Email Templates{ready ? ` (${templates.length})` : ''}
        </h2>
      </div>
      {unreadable ? (
        <div role="alert" className="p-6 text-center">
          <p className="text-sm font-semibold text-gray-900">
            {state === 'blocked'
              ? 'Can’t reach the server, so your templates haven’t loaded.'
              : state === 'failed'
                ? (query.error?.message || 'We couldn’t load your email templates.')
                : 'Your email templates came back in a shape we couldn’t read.'}
          </p>
          <p className="mx-auto mt-1.5 max-w-sm text-[12.5px] text-gray-500">
            {state === 'ready'
              ? 'This is not an empty set of templates — the list could not be read.'
              : 'This is not a firm with no templates — the list never arrived.'}
          </p>
          <button type="button" onClick={() => query.refetch()} className="btn-cb-toolbar mt-3.5">
            Try again
          </button>
        </div>
      ) : !ready ? (
        <div className="p-8 flex justify-center"><LoadingSpinner message="Loading templates..." /></div>
      ) : templates.length === 0 ? (
        <p className="p-8 text-sm text-gray-500 text-center">No email templates found</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {templates.map((t, i) => {
            const id = t.TemplateId || t.Id || t.id || i
            const name = t.TemplateName || t.Name || t.name || `Template ${i + 1}`
            const section = t.Section || t.section || ''
            return (
              <div key={id} className="px-5 py-3 hover:bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">{name}</p>
                  {section && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{section}</span>}
                </div>
                {t.Subject && <p className="text-xs text-gray-500 mt-0.5">Subject: {t.Subject}</p>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const ComposeTab = () => {
  const [to, setTo] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  // The last send's outcome: `{kind:'accepted', draft}` or `{kind:'problem', message}`.
  // It stays until the next send — never on a timer — because the warnings in it are
  // exactly what someone must read before deciding whether to press Send again.
  const [outcome, setOutcome] = useState(null)
  const toRef = useRef(null)

  /*
   * Spec 156 (CC-53907). This said "Email sent" on ANY 200 — and the old relay answered
   * CB's refusal `{success:false, error:"From Email address is missing"}` with a 200,
   * so every send from this tab was announced and none went out (measured live
   * 2026-10-09). cb-api now refuses with a non-2xx carrying the reason, and a 200 says
   * `accepted` or `unconfirmed`.
   *
   * Review fixes (spec 156 §9): `accepted` is NOT "sent" — CB returns a real email id even
   * when its O365 send failed — so it reads "Accepted by Centerbase for delivery", and the
   * cleared draft can be restored. A request that ends with no answer may have reached CB,
   * so it says the email may already have gone out (`utils/emailComposeOutcome.js`). The
   * outcome is written into live regions that are in the page before the send, not a toast
   * that screen readers never hear and that vanished after 4 seconds.
   */
  const sendMutation = useMutation({
    mutationFn: (draft) => emailService.send(draft),
    // Cleared first, so a repeated outcome is announced again.
    onMutate: () => setOutcome(null),
    onSuccess: (res, draft) => {
      const result = outcomeOfAnswer(res)
      if (result.kind !== 'accepted') {
        setOutcome(result)
        return
      }
      setOutcome({ kind: 'accepted', draft })
      setTo('')
      setSubject('')
      setBody('')
    },
    onError: (err) => setOutcome(outcomeOfFailure(err)),
  })

  const accepted = outcome?.kind === 'accepted'
  const problem = outcome?.kind === 'problem'
  // Restore is offered only into an empty form, so it can never overwrite a new draft.
  const canRestore = accepted && !to && !subject && !body
  const restoreDraft = () => {
    setTo(outcome.draft.to)
    setSubject(outcome.draft.subject)
    setBody(outcome.draft.body)
    // The button unmounts once the form is full again; focus goes where the work is.
    toRef.current?.focus()
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-4">Compose Email</h2>
      <div className="space-y-4 max-w-2xl">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <input
            ref={toRef}
            type="email"
            aria-label="Recipient email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="recipient@example.com"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
          <input
            type="text"
            aria-label="Email subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
          <textarea
            aria-label="Email body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            placeholder="Type your message..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30 resize-y"
          />
        </div>
        <button
          onClick={() => sendMutation.mutate({ to, subject, body })}
          disabled={!to || !subject || sendMutation.isPending}
          className="px-6 py-2.5 bg-cb-accent text-white rounded-lg text-sm font-medium hover:bg-cb-accent/90 disabled:opacity-50"
        >
          {sendMutation.isPending ? 'Sending...' : 'Send Email'}
        </button>
        {/*
          Both regions are always mounted (empty until a send answers): a live region
          inserted together with its text is not reliably announced. One wrapper, so the
          empty regions add no `space-y` margins. The Restore button sits OUTSIDE the
          polite region, so withdrawing it does not re-read the status.
        */}
        <div>
          <div
            role="status"
            aria-live="polite"
            data-testid="compose-status"
            className={accepted ? 'rounded-lg border border-gray-200 bg-white p-3' : ''}
          >
            {accepted && (
              <>
                <p className="text-sm font-semibold text-gray-900">{ACCEPTED_TITLE}</p>
                <p className="mt-1 text-[12.5px] text-gray-600">{ACCEPTED_NOTE}</p>
              </>
            )}
          </div>
          {canRestore && (
            <button type="button" onClick={restoreDraft} className="btn-cb-toolbar mt-2">
              Restore message
            </button>
          )}
          <div
            role="alert"
            data-testid="compose-alert"
            className={problem ? 'rounded-lg border border-amber-200 bg-amber-50 p-3' : ''}
          >
            {problem && <p className="text-sm text-amber-900">{outcome.message}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

const SettingsTab = () => {
  const query = useQuery({
    queryKey: ['email-settings'],
    queryFn: () => emailService.getSettings(),
  })
  const { data } = query
  /*
   * CC-53132. "No mail settings configured" is the sentence an admin would act on by
   * going and configuring a mail server that is, in fact, already configured — and
   * with the read unresolved it was the sentence they got. Same shape as the tab
   * above; `idle` unreachable, so folded into loading.
   */
  const state = useReadState('email-settings', query)
  const ready = state === 'ready'
  const unreadable = state === 'blocked' || state === 'failed'

  const settings = data?.data

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-4">Mail Server Settings</h2>
      {unreadable ? (
        <div role="alert">
          <p className="text-sm font-semibold text-gray-900">
            {state === 'blocked'
              ? 'Can’t reach the server, so we haven’t loaded your mail settings.'
              : 'We haven’t loaded your mail settings.'}
          </p>
          <p className="mt-1.5 max-w-lg text-[12.5px] text-gray-500">
            This does not mean nothing is configured — we never asked.
          </p>
          <button type="button" onClick={() => query.refetch()} className="btn-cb-toolbar mt-3.5">
            Try again
          </button>
        </div>
      ) : !ready ? (
        <LoadingSpinner message="Loading settings..." />
      ) : !settings ? (
        <p className="text-sm text-gray-500">No mail settings configured</p>
      ) : (
        <pre className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600 overflow-x-auto max-h-96">
          {JSON.stringify(settings, null, 2)}
        </pre>
      )}
    </div>
  )
}

export default Email
